export const COOKIE_CONSENT_NAME = 'freedom_cookie_consent';
export const COOKIE_CONSENT_VERSION = 2;
export const COOKIE_CONSENT_MAX_AGE_SECONDS = 180 * 24 * 60 * 60;
export const COOKIE_CONSENT_CHANGED_EVENT = 'freedom:cookie-consent-changed';
export const COOKIE_SETTINGS_OPEN_EVENT = 'freedom:open-cookie-settings';
export const COOKIE_CONSENT_BROADCAST_CHANNEL = 'freedom-cookie-consent';

export type CookieConsentCategory = 'necessary' | 'preferences' | 'analytics';

export interface CookieConsent {
  version: typeof COOKIE_CONSENT_VERSION;
  necessary: true;
  preferences: boolean;
  analytics: boolean;
  decidedAt: string;
}

export interface CookieSerializationOptions {
  path?: string;
  maxAgeSeconds?: number;
  sameSite?: 'Strict' | 'Lax' | 'None';
  secure?: boolean;
}

/** Parse a Cookie request header or document.cookie without mutating browser state. */
export function parseCookieHeader(cookieHeader: string): Record<string, string> {
  const cookies: Record<string, string> = {};
  for (const part of cookieHeader.split(';')) {
    const separator = part.indexOf('=');
    if (separator < 1) continue;
    const name = part.slice(0, separator).trim();
    if (!name) continue;
    cookies[name] = part.slice(separator + 1).trim();
  }
  return cookies;
}

/** Build a browser cookie assignment string without mutating browser state. */
export function serializeCookie(name: string, value: string, options: CookieSerializationOptions = {}): string {
  const path = options.path ?? '/';
  const maxAgeSeconds = options.maxAgeSeconds ?? COOKIE_CONSENT_MAX_AGE_SECONDS;
  const sameSite = options.sameSite ?? 'Lax';
  const parts = [
    `${name}=${encodeURIComponent(value)}`,
    `Path=${path}`,
    `Max-Age=${Math.max(0, Math.floor(maxAgeSeconds))}`,
    `SameSite=${sameSite}`,
  ];
  if (options.secure) parts.push('Secure');
  return parts.join('; ');
}

export function createCookieConsent(preferences: boolean, analytics: boolean, decidedAt: Date = new Date()): CookieConsent {
  return {
    version: COOKIE_CONSENT_VERSION,
    necessary: true,
    preferences,
    analytics,
    decidedAt: decidedAt.toISOString(),
  };
}

export function readCookieConsent(cookieHeader: string): CookieConsent | null {
  const raw = parseCookieHeader(cookieHeader)[COOKIE_CONSENT_NAME];
  if (!raw) return null;

  try {
    const candidate = JSON.parse(decodeURIComponent(raw)) as Partial<CookieConsent>;
    if (!isCookieConsent(candidate)) return null;
    return candidate as CookieConsent;
  } catch {
    return null;
  }
}

export function isCookieConsent(candidate: unknown): candidate is CookieConsent {
  if (typeof candidate !== 'object' || candidate === null || Array.isArray(candidate)) return false;
  const value = candidate as Partial<CookieConsent>;
  return value.version === COOKIE_CONSENT_VERSION
    && value.necessary === true
    && typeof value.preferences === 'boolean'
    && typeof value.analytics === 'boolean'
    && typeof value.decidedAt === 'string'
    && Number.isFinite(Date.parse(value.decidedAt));
}

export function sameCookieConsent(left: CookieConsent | null, right: CookieConsent | null): boolean {
  return left !== null && right !== null
    && left.version === right.version
    && left.necessary === right.necessary
    && left.preferences === right.preferences
    && left.analytics === right.analytics
    && left.decidedAt === right.decidedAt;
}

export function serializeCookieConsent(consent: CookieConsent, secure = false): string {
  return serializeCookie(COOKIE_CONSENT_NAME, JSON.stringify(consent), {
    maxAgeSeconds: COOKIE_CONSENT_MAX_AGE_SECONDS,
    path: '/',
    sameSite: 'Lax',
    secure,
  });
}

export function consentAllows(consent: CookieConsent | null, category: CookieConsentCategory): boolean {
  if (category === 'necessary') return true;
  return consent?.[category] === true;
}

export function persistCookieConsent(
  consent: CookieConsent,
  target: Pick<Document, 'cookie'> = document,
  secure = typeof window !== 'undefined' && window.location.protocol === 'https:',
): void {
  target.cookie = serializeCookieConsent(consent, secure);
}

/** Write then read the cookie back; browsers may silently ignore blocked cookie writes. */
export function persistAndReadCookieConsent(
  consent: CookieConsent,
  target: Pick<Document, 'cookie'> = document,
  secure = typeof window !== 'undefined' && window.location.protocol === 'https:',
): CookieConsent | null {
  persistCookieConsent(consent, target, secure);
  return readCookieConsent(target.cookie);
}

/** Lets an existing settings menu reopen the consent dialog without coupling to React state. */
export function openCookieSettings(target: Pick<Window, 'dispatchEvent'> = window): void {
  target.dispatchEvent(new Event(COOKIE_SETTINGS_OPEN_EVENT));
}
